const DEFAULT_SELECTOR = 'a[href]';
const DEFAULT_EVENTS = ['pointerover', 'focusin', 'touchstart'];
const prefetchedUrls = new Set();

export function attachPrefetchOnHover(root, options = {}) {
    if (!root || (options.mediaQuery && !matchesMediaQuery(options.mediaQuery))) {
        return () => {};
    }

    const selector = options.selector || DEFAULT_SELECTOR;
    const events = options.events || DEFAULT_EVENTS;
    const prefetch = (event) => {
        const link = event.target?.closest?.(selector);

        if (!link || !root.contains(link) || shouldSkipPrefetchLink(link)) {
            return;
        }

        prefetchLinkHref(link.getAttribute('href'), options);
    };

    events.forEach((eventName) => root.addEventListener(eventName, prefetch, { passive: true }));

    return () => events.forEach((eventName) => root.removeEventListener(eventName, prefetch));
}

export function prefetchLinkHref(href, options = {}) {
    const url = normalizeUrl(href, options);

    if (!url || hasPrefetched(url.href)) {
        return false;
    }

    // The browser schedules the page fetch without blocking the current interaction.
    const link = document.createElement('link');
    link.rel = options.rel || 'prefetch';
    link.href = url.href;
    link.setAttribute('as', options.as || 'document');

    document.head.appendChild(link);
    prefetchedUrls.add(url.href);

    return true;
}

function matchesMediaQuery(mediaQuery) {
    if (!window.matchMedia) {
        return true;
    }

    return window.matchMedia(mediaQuery).matches;
}

function shouldSkipPrefetchLink(link) {
    return (
        link.hasAttribute('download') ||
        link.hasAttribute('data-no-prefetch') ||
        link.getAttribute('target')?.toLowerCase() === '_blank' ||
        link.relList?.contains('nofollow')
    );
}

function normalizeUrl(href, options) {
    if (!href) {
        return null;
    }

    const cleanHref = href.trim();

    if (!cleanHref || cleanHref.startsWith('#')) {
        return null;
    }

    const url = new URL(cleanHref, window.location.href);

    if (!['http:', 'https:'].includes(url.protocol)) {
        return null;
    }

    if (url.origin !== window.location.origin && !options.includeExternal) {
        return null;
    }

    if (isSameDocumentHash(url)) {
        return null;
    }

    return url;
}

function isSameDocumentHash(url) {
    const currentUrl = new URL(window.location.href);

    return (
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search &&
        (url.hash !== '' || url.href.replace(/#$/, '') === currentUrl.href.replace(/#$/, ''))
    );
}

function hasPrefetched(href) {
    return (
        prefetchedUrls.has(href) ||
        Array.from(document.head.querySelectorAll('link[rel="prefetch"]')).some((link) => link.href === href)
    );
}
